import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { TransactionForm, createEmptyItem } from '@/components/transaction/transaction-form'
import type { ItemRow } from '@/components/transaction/transaction-form'
import { useProfiles, useCategories, useSuggestions, useCreateTransaction, useTags, useLastUsedTags, useCreateTag } from '@/lib/hooks'
import { useAuth } from '@/lib/auth-context'
import { validateAndToast, createTransactionSchema } from '@/lib/schemas'
import { todayString } from '@/lib/utils'


export default function NewTransactionPage() {
  const navigate = useNavigate()
  const { data: profiles = [] } = useProfiles()
  const { data: categories = [] } = useCategories()
  const { storeNames, itemNames } = useSuggestions()
  const { user: currentUser } = useAuth()
  const { data: tags = [] } = useTags()
  const { data: lastUsedTags = [] } = useLastUsedTags()
  const createTagMutation = useCreateTag()
  const createTx = useCreateTransaction()

  const [date, setDate] = useState(todayString)
  const [storeName, setStoreName] = useState('')
  const [memo, setMemo] = useState('')
  const [items, setItems] = useState<ItemRow[]>([createEmptyItem()])
  const [paidBy, setPaidBy] = useState<string | null>(null)
  const [scope, setScope] = useState<'household' | 'personal'>('household')
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const [taxAmount, setTaxAmount] = useState(0)

  const effectivePaidBy = scope === 'personal' ? (currentUser?.id ?? '') : (paidBy ?? currentUser?.id ?? '')

  const handleScopeChange = (newScope: 'household' | 'personal') => {
    setScope(newScope)
    if (newScope === 'personal' && currentUser) {
      setPaidBy(currentUser.id)
    }
  }

  const handleSave = () => {
    if (createTx.isPending) return

    const validItems = items.filter((i) => i.name.trim() !== '')
    const totalAmount = validItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0) + taxAmount

    const payload = validateAndToast(createTransactionSchema, {
      paid_by: effectivePaidBy || '',
      date,
      store_name: storeName || undefined,
      memo: memo || undefined,
      total_amount: totalAmount,
      tax_amount: taxAmount,
      input_method: 'manual',
      items: validItems.map((item) => ({
        name: item.name,
        amount: Number(item.amount) || 0,
        category_id: item.category_id || null,
      })),
      scope,
      tag_ids: selectedTagIds.length > 0 ? selectedTagIds : undefined,
    })

    if (!payload) return
    createTx.mutate(payload, {
      onSuccess: () => navigate('/transactions', { replace: true }),
    })
  }

  return (
    <div className="mx-auto max-w-lg space-y-4 p-4">
      <Card>
        <CardHeader>
          <CardTitle>手動入力</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <TransactionForm
            date={date}
            storeName={storeName}
            memo={memo}
            items={items}
            paidBy={effectivePaidBy}
            profiles={profiles}
            categories={categories}
            scope={scope}
            taxAmount={taxAmount}
            tags={tags}
            selectedTagIds={selectedTagIds}
            lastUsedTags={lastUsedTags}
            storeNameSuggestions={storeNames}
            itemNameSuggestions={itemNames}
            onDateChange={setDate}
            onStoreNameChange={setStoreName}
            onMemoChange={setMemo}
            onItemsChange={setItems}
            onPaidByChange={setPaidBy}
            onTaxAmountChange={setTaxAmount}
            onScopeChange={handleScopeChange}
            onTagIdsChange={setSelectedTagIds}
            onCreateTag={(data) => createTagMutation.mutate(data)}
          />

          <Button
            type="button"
            className="w-full"
            onClick={handleSave}
            disabled={createTx.isPending}
          >
            {createTx.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
            保存
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
